import { Avatar, Card, Divider, Group, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { iconProps } from 'components/icon-props';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import type { Mail } from 'modules/Mails/hooks';
import { isNonNullish } from 'remeda';
import type { LightDebtor } from 'shared/hooks/use-load-light-debtors';
import { CollapsedCategoryChips } from './CollapsedCategoryChips';
import { ThreadMessageAttachments } from './ThreadMessageAttachments';
import { ThreadMessageBody } from './ThreadMessageBody';
import { ThreadMessageCategories } from './ThreadMessageCategories';
import { ThreadMessageHead } from './ThreadMessageHead';
import { ThreadMessageProvider } from './useThreadMessage';
import { getPreview } from './utils';

type ThreadMessageProps = {
  mail: Mail;
  isOpen: boolean;
  debtor: LightDebtor | null;
  onExpandMail: () => void;
};

export const ThreadMessage = ({ mail, isOpen, debtor, onExpandMail }: ThreadMessageProps) => {
  const company = useCompanyConfiguration();

  const customDomain = company.custom_domain ?? null;
  const isOutbound = isNonNullish(customDomain) && mail.fromMail.includes(customDomain);
  const senderName =
    !isOutbound && isNonNullish(debtor) ? debtor.fullName : (mail.fromName ?? mail.fromMail);
  const dateLabel = mail.date?.format('MMM D, HH:mm') ?? '';

  if (!isOpen) {
    return (
      <UnstyledButton
        onClick={onExpandMail}
        p="space3"
        bg="gray.0"
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          borderRadius: 'var(--mantine-radius-md)',
          textAlign: 'left',
        }}
      >
        <Group gap="space3" wrap="nowrap">
          <IconChevronRight {...iconProps(14)} color="var(--mantine-color-dimmed)" />
          <Avatar name={senderName} size={24} radius="xl" color={isOutbound ? 'blue' : 'violet'} />
          <Text size="sm" fw={500}>
            {senderName}
          </Text>
          <Text size="xs" c="dimmed">
            ·
          </Text>
          <Text size="xs" c="dimmed">
            {dateLabel}
          </Text>
          <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
            {getPreview(mail.body)}
          </Text>
          {!isOutbound && mail.categories.length > 0 && (
            <CollapsedCategoryChips categories={mail.categories} />
          )}
        </Group>
      </UnstyledButton>
    );
  }

  return (
    <ThreadMessageProvider
      value={{ mail, isOutbound, senderName, dateLabel, onCollapse: onExpandMail }}
    >
      <Card withBorder padding={0} radius="md">
        <ThreadMessageHead />
        <Divider />
        <ThreadMessageCategories />
        <Divider />
        <ThreadMessageBody />
        {mail.attachments.length > 0 && (
          <>
            <Divider />
            <ThreadMessageAttachments />
          </>
        )}
      </Card>
    </ThreadMessageProvider>
  );
};
